import React, { Fragment, useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get, isNil } from 'lodash';
import { CheckCircle } from "lucide-react"

import componentStyles from 'components/BankStatements/ui/css/styles.scss';
import { formatDate } from 'utils/bankStatements';
import Header from 'components/Header';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';
import { nextAction } from 'actions/workflow';
import { Card, CardContent } from 'components/BankStatements/ui/card';

import style from './style.scss';

const BankVerificationReview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const key = searchParams.get('key');
  const applicationId = useSelector(state => get(state, 'workflow.entityId', get(state, 'workflow.attributes.entityId', '')));
  const data = useSelector(state => get(state, 'workflow.data.data') || get(state, 'workflow.state.data'));
  const statements = (get(data, 'bankStatementData') || []).filter((bs) => get(bs, 'processingStatus') === 'Done');

  useEffect(() => {
    if (!key) {
      navigate('/dashboard');
    } else if (isNil(data)) {
      navigate(`/applications/fsl/checkin?key=${key}`);
    }
  }, []);

  const handleSubmit = (isSubmitted) => {
    setIsLoading(true);
    dispatch(nextAction({
      url: `/workflows/fsl/${key}/next`,
      data: {
        isSubmitted,
      },
      success: (res) => {
        const nextUrl = get(res, 'data.url') || get(res, 'state.url');
        if (nextUrl) navigate(nextUrl);
        setIsLoading(false);
      },
      fail: (error) => {
        setIsLoading(false);
        console.log(error);
      },
    }));
  };

  return (
    <Fragment>
      <Header />
      <section className="container section">
        <div className={cn('use-tailwind grid-container fluid', componentStyles['bank-statement-container'], style['bank-verification-review-page'])}>
          <div className="grid-container">
            <Grid className="grid-margin-x max-limited">
              {applicationId ? (
                <div className="cell small-12" style={{ textAlign: 'left' }}>
                  <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                </div>
              ) : null}

              <Column className="small-12">
                <div className="my-6">
                  <p className="text-gray-800">
                    Ready to finalize? Click <strong>'Submit All Statements'</strong> to complete this application process, or{" "}
                    <strong>'Add Bank or Statements'</strong>, if needed.
                  </p>
                </div>
              </Column>
              {statements && statements.length > 0 ? (
                <Column className="small-12 hidden md:!block">
                  <Card className="upload-section">
                    <CardContent className="p-0">
                      <div className="flex flex-col" style={{ marginBottom: '20px' }}>
                        <h2 className="text-xl font-medium text-gray-800">All Statements</h2>
                      </div>
                      <div className="table-wrapper">
                        <table className="statement-table">
                          <thead>
                            <tr>
                              <th>Bank Name</th>
                              <th>Account Number</th>
                              <th>Statement Period</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statements
                              // Add deduplication filter to ensure unique statement IDs
                              .filter((statement, index, self) => index === self.findIndex(s => s.bankStatementId === statement.bankStatementId))
                              // Sort by statement date (newest first)
                              .sort((a, b) => {
                                // Use statementEndDate for sorting (newest first)
                                const dateA = new Date(a.statementEndDate || '');
                                const dateB = new Date(b.statementEndDate || '');
                                return dateA.getTime() - dateB.getTime();
                              })
                              .map(statement => (
                                <tr
                                  key={statement.bankStatementId}
                                  className={statement.isDeleted ? 'opacity-50' : ''}
                                >
                                  <td>
                                    {statement.bankName}
                                  </td>
                                  <td>
                                    {(statement.accountNumbers && statement.accountNumbers.join(', ')) || 'N/A'}
                                  </td>
                                  <td>
                                    {formatDate(statement.statementStartDate)}
                                    {' '}
                                    to
                                    {' '}
                                    {formatDate(statement.statementEndDate)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </Column>
              ) : null}

              {statements && statements.length > 0 ? (
                <Column className="small-12 md:hidden mb-4">
                  <h2 className="text-lg font-bold mb-4 p-0">All Statements</h2>
                  <div className="space-y-4">
                    {statements.map((statement, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardContent className="p-0">
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex justify-between items-start">
                              <h3 className="font-bold text-gray-800">{statement.bankName}</h3>
                            </div>
                          </div>

                          <div className="px-4 py-3">
                            <div className="text-sm text-gray-500">Statement Period</div>
                            <div className="font-medium">
                              {formatDate(statement.statementStartDate)}
                              {' '}
                              to
                              {' '}
                              {formatDate(statement.statementEndDate)}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </Column>
              ) : null}

              <Column className="small-12">
                <div className="flex flex-col md:flex-row gap-4 mt-2">
                  <div className="md:w-1/2 w-full">
                    <Button
                      onClick={() => handleSubmit(false)}
                      disabled={isLoading}
                      className="text-sm md:text-base w-full h-[56px] bg-white text-green-600 border-2 border-green-600 px-6 rounded-md text-center font-medium border-solid hover:bg-green-50 transition-colors flex flex-col justify-center"
                    >
                      Add Another Bank or Upload More Statements
                    </Button>
                  </div>

                  <div className="md:w-1/2 w-full">
                    <Button
                      onClick={() => handleSubmit(true)}
                      disabled={isLoading}
                      className="text-sm md:text-base w-full h-[56px] bg-green-600 text-white px-6 rounded-md text-center font-bold hover:bg-green-700 transition-colors text-lg shadow-md flex items-center justify-center"
                    >
                      <CheckCircle className="h-6 w-6 mr-2" />
                      Submit all statements
                    </Button>
                  </div>
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default BankVerificationReview;
